/**
 * @file NewCustomerFormulationModal.js
 * @version 1.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-16
 * @description "New formulation for <customer>" — a customer's new blend in one
 *              window and one save.
 *
 *              It used to take three: Add Item (the inventory item, pushed to
 *              Xero), Add Formulation (the recipe for that item), and
 *              Formulations → Assign to customer (the customer and the product
 *              type — the customer's blend line, Milkers, Beef, Calf…). This
 *              collects what those three collect, with their own pieces rather
 *              than copies of them:
 *
 *                customer + product type   CustomerBlendPicker (Assign to customer)
 *                the item                  InventoryItemFields (Add Item), its
 *                                          validation and its wording check
 *                the formulation           FormulationEditor (Add Formulation),
 *                                          including "Read from a file"
 *
 *              and saves through POST /api/formulations/for-customer, which
 *              writes the three in one transaction and pushes the item to Xero
 *              after it commits. If Xero is unavailable the save still stands —
 *              exactly as Add Item behaves — and this says so: the inventory
 *              sync sends the item later.
 *
 *              Blends only (sales account 202, or 207A for tote bags): only a
 *              blend is filed under a customer. The item starts on the accounts
 *              nearly every blend uses (202 / 312, zero-rated, tonnes).
 *
 *              Needs both permissions the three steps belong to —
 *              inventory.edit and formulations.edit — and is never offered to a
 *              read-only account; callers disable their button with the reason.
 *              useNewCustomerFormulationAccess() is that test, shared.
 *
 *              1.1.0 (2026-10-14): the formulation's date is the user's —
 *              empty until chosen, required, not in the future — as on Add
 *              Formulation (FormulationDateField, via FormulationEditor).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import AlertDialog from './AlertDialog';
import LoadingSpinner from './LoadingSpinner';
import CustomerBlendPicker, { useBlendAccounts } from './CustomerBlendPicker';
import CustomerProductsModal from './CustomerProductsModal';
import InventoryItemFields, {
  BLANK_ITEM_FORM, validateItemForm, itemSaveBody,
  checkItemText, textFieldsToCheck, useItemFormLists, ItemTextWarningDialog
} from './InventoryItemFields';
import {
  FormulationEditor, useFormulationExtractAvailable, useAddFormulationComponent
} from './FormulationModal';
import FormulationScreenshotModal from './FormulationScreenshotModal';
import { formulationDateProblem } from '../utils/formulationDates';
import useCustomerBlends from '../hooks/useCustomerBlends';
import useAccess from '../hooks/useAccess';
import { useSlideOutButton } from './SlideOutButton';
import './CustomerBlendPicker.css';
import './Formulations.css';
import './NewCustomerFormulationModal.css';

// The sales accounts that make an item a blend — the server's
// formulationService.BLEND_ACCOUNT_CODES. Only these can be filed under a
// customer.
const BLEND_SALES_ACCOUNTS = ['202', '207A'];

// Where nearly every blend item starts: 855 of 859 blends on 202 are bought on
// 312, zero-rated both ways, and sold by the tonne. The tax types are those
// accounts' Xero defaults, set here rather than left to arrive with the account
// list — a blank purchase tax type would go to Xero as 20% (createItem's
// default) if Create were pressed before the list loaded.
const BLEND_ITEM_DEFAULTS = {
  ...BLANK_ITEM_FORM,
  // A blend is bought and sold (both accounts below); Add Item's own blank
  // form starts with neither ticked (2026-10-16), this preset ticks both.
  is_sold: true,
  is_purchased: true,
  sales_account_code: '202',
  sales_tax_type: 'ZERORATEDOUTPUT',
  purchase_account_code: '312',
  purchase_tax_type: 'ZERORATEDINPUT',
  unit_weight_kg: '1000'
};

/**
 * Can this user create a formulation for a customer? Both permissions the three
 * separate steps need, and not a view-only account.
 *
 * @returns {{ allowed: boolean, reason: string|null }}
 */
export function useNewCustomerFormulationAccess() {
  const { can, readOnly, badgeLabel } = useAccess();
  if (readOnly) return { allowed: false, reason: `${badgeLabel} accounts have view-only access` };
  const missing = [
    !can('inventory.edit') && 'Edit inventory items',
    !can('formulations.edit') && 'Edit formulations'
  ].filter(Boolean);
  if (missing.length) {
    return { allowed: false, reason: `Needs the ${missing.join(' and ')} permission${missing.length > 1 ? 's' : ''}` };
  }
  return { allowed: true, reason: null };
}

// The footer's own component, so useSlideOutButton runs in the render that
// mounts the buttons (Modal adds its portal content a cycle late).
function Footer({ saving, saveBlockedReason, onCancel, onSave }) {
  useSlideOutButton([saving, !!saveBlockedReason]);
  return (
    <div className="buttons slideout">
      <div>
        <button data-read-safe className="btn-round btn-danger" onClick={onCancel} disabled={saving}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button
          className="btn-slideout"
          onClick={onSave}
          disabled={saving || !!saveBlockedReason}
          title={saveBlockedReason || 'Create the item, its formulation and its place under the customer'}
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
          <span className="btn-label">{saving ? 'Saving...' : 'Create'}</span>
        </button>
      </div>
    </div>
  );
}

/**
 * @param {object?}  account    the customer to pre-fill: { id, company_name, account_no }
 * @param {function} onClose    always called on the way out — refresh what is behind
 * @param {function} onCreated  (result) after a successful save, before onClose
 */
function NewCustomerFormulationModal({ account = null, onClose, onCreated }) {
  const access = useNewCustomerFormulationAccess();
  const accounts = useBlendAccounts();
  const { blends, loading: blendsLoading } = useCustomerBlends();

  const [placement, setPlacement] = useState({
    accountId: account?.id ? String(account.id) : null,
    name: ''
  });
  const [viewingCustomer, setViewingCustomer] = useState(null);

  const [formData, setFormData] = useState(BLEND_ITEM_DEFAULTS);
  const [formErrors, setFormErrors] = useState({});
  const lists = useItemFormLists(true, setFormData);
  const textAcceptedRef = useRef(false);
  const [textWarning, setTextWarning] = useState(null);

  const [components, setComponents] = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(true);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState('');
  // The formulation's date: empty until the user chooses it, required.
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [showScreenshot, setShowScreenshot] = useState(false);
  const extractAvailable = useFormulationExtractAvailable();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [xeroLater, setXeroLater] = useState(null);
  const topRef = useRef(null);

  const loadComponents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/formulations/components`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load formulation fields');
      setComponents(data.components || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setComponentsLoading(false);
    }
  }, []);
  useEffect(() => { loadComponents(); }, [loadComponents]);
  const { adding, addComponent } = useAddFormulationComponent(loadComponents);

  /* A pre-filled customer with exactly one line: start on it, as picking the
     customer would. Once, when the lines arrive — never over a choice. */
  const prefilledLine = useRef(false);
  useEffect(() => {
    if (prefilledLine.current || blendsLoading || !placement.accountId) return;
    prefilledLine.current = true;
    const lines = blends.filter(b => String(b.account_id) === String(placement.accountId));
    if (lines.length === 1) setPlacement(prev => (prev.name ? prev : { ...prev, name: lines[0].name }));
  }, [blends, blendsLoading, placement.accountId]);

  const customer = placement.accountId ? accounts.byId.get(String(placement.accountId)) : null;
  const customerName = customer?.company_name || account?.company_name || null;

  const showError = (message) => {
    setError(message);
    topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  /** Everything the server would refuse that can be seen from here. */
  const validate = () => {
    const errors = validateItemForm(formData);
    const salesCode = String(formData.sales_account_code || '').trim().toUpperCase();
    if (!BLEND_SALES_ACCOUNTS.includes(salesCode)) {
      errors.sales_account_code = 'A blend: 202, or 207A for tote bags';
    }
    setFormErrors(errors);
    if (!placement.accountId) return 'Choose the customer this formulation is for.';
    if (!String(placement.name || '').trim()) {
      return 'Choose the product type — the customer\'s blend line, such as Milkers or Beef.';
    }
    if (errors.sales_account_code) {
      return 'Only a blend can be filed under a customer: set the item\'s sales account (under Sold) to 202, or 207A for tote bag blends.';
    }
    if (Object.keys(errors).length) return 'Please fill in the item\'s required fields.';
    const dateProblem = formulationDateProblem(date);
    setDateError(dateProblem || '');
    if (dateProblem) return dateProblem;
    return null;
  };

  const save = async () => {
    setError('');
    const problem = validate();
    if (problem) { showError(problem); return; }

    if (!textAcceptedRef.current) {
      const findings = await checkItemText(textFieldsToCheck(formData));
      if (findings.length) { setTextWarning(findings); return; }
    }

    setSaving(true);
    try {
      // Blanks are stripped so "not measured" stays distinct from an explicit 0.
      const componentValues = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      const res = await fetch(`${API_URL}/api/formulations/for-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          item: itemSaveBody(formData),
          formulation: { component_values: componentValues, notes, iteration_date: date },
          placement: { account_id: Number(placement.accountId), line_name: String(placement.name).trim() }
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not create the formulation. Nothing was saved.');
      setSaving(false);
      onCreated?.(data);
      if (data.xero && !data.xero.pushed) {
        // Saved here; Xero has not got the item yet. Said, not hidden.
        setXeroLater(data);
        return;
      }
      onClose();
    } catch (err) {
      setSaving(false);
      // Nothing was written: the edits are still here for Create to retry.
      showError(err.message);
    }
  };

  const saveBlockedReason = access.allowed ? null : access.reason;

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        closeOnEscape={!saving}
        title={customerName ? `New formulation for ${customerName}` : 'New formulation for a customer'}
        className="new-customer-formulation"
        size="lg"
        // A long form: a stray click outside must not throw it away.
        closeOnOverlayClick={false}
        footer={(
          <Footer
            saving={saving}
            saveBlockedReason={saveBlockedReason}
            onCancel={onClose}
            onSave={save}
          />
        )}
      >
        <div ref={topRef} />
        {error && <div className="formulation-error">{error}</div>}
        {notice && <div className="formulation-notice">{notice}</div>}
        {saveBlockedReason && <div className="formulation-notice">{saveBlockedReason}. You can look, but not create.</div>}

        <section className="ncf-section">
          <h3 className="ncf-section-title">Customer &amp; product type</h3>
          <p className="ncf-hint">
            The product type is the customer's blend line — Milkers, Beef, Calf. Pick one they have, or type a new one.
          </p>
          {accounts.loading ? (
            <LoadingSpinner />
          ) : (
            <CustomerBlendPicker
              accountOptions={accounts.options}
              accountsById={accounts.byId}
              blends={blends}
              value={placement}
              onChange={setPlacement}
              onViewCustomer={setViewingCustomer}
              disabled={saving}
            />
          )}
          {accounts.error && <p className="ncf-hint ncf-hint-error">{accounts.error}</p>}
        </section>

        <section className="ncf-section">
          <h3 className="ncf-section-title">Item</h3>
          <p className="ncf-hint">
            As Add Item: created here and sent to Xero. A blend — sales account 202, or 207A for tote bags.
          </p>
          {/* The item fields are styled under .item-modal (SearchInventory.css,
              InventoryItemFormModal.css). */}
          <div className="item-modal ncf-item">
            <InventoryItemFields
              formData={formData}
              setFormData={setFormData}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              lists={lists}
              onTextEdited={() => { textAcceptedRef.current = false; }}
            />
          </div>
        </section>

        <section className="ncf-section">
          <h3 className="ncf-section-title">Formulation</h3>
          {componentsLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="formulation-modal-body">
              <FormulationEditor
                components={components}
                form={values}
                setForm={setValues}
                notes={notes}
                setNotes={setNotes}
                onAddComponent={addComponent}
                adding={adding}
                extractAvailable={extractAvailable}
                onExtractScreenshot={() => setShowScreenshot(true)}
                date={date}
                setDate={(d) => { setDate(d); setDateError(''); }}
                dateError={dateError}
              />
            </div>
          )}
        </section>
      </Modal>

      {showScreenshot && (
        <FormulationScreenshotModal
          components={components}
          existingValues={values}
          onCreateComponent={addComponent}
          onClose={() => setShowScreenshot(false)}
          onApply={(applied) => {
            // Merge rather than replace, as Add Formulation does.
            setValues((prev) => ({ ...prev, ...applied }));
            setShowScreenshot(false);
            setNotice(
              `Applied ${Object.keys(applied).length} figure(s) from the file. `
              + 'Check them, then create.'
            );
          }}
        />
      )}

      {textWarning && (
        <ItemTextWarningDialog
          findings={textWarning}
          onConfirm={() => {
            setTextWarning(null);
            textAcceptedRef.current = true;
            save();
          }}
          onCancel={() => setTextWarning(null)}
        />
      )}

      {viewingCustomer && (
        <CustomerProductsModal account={viewingCustomer} onClose={() => setViewingCustomer(null)} />
      )}

      <AlertDialog
        isOpen={!!xeroLater}
        type="warning"
        title="Saved — not in Xero yet"
        message={xeroLater
          ? `${xeroLater.item?.item_code} ${xeroLater.item?.item_name}, its formulation and its place under `
            + `${xeroLater.placement?.account_name} (${xeroLater.placement?.line_name}) are saved. `
            + `The item could not be created in Xero: ${xeroLater.xero?.reason || 'no reason given'}. `
            + 'It will be sent to Xero automatically at the next inventory sync.'
          : ''}
        onClose={() => { setXeroLater(null); onClose(); }}
      />
    </>
  );
}

export default NewCustomerFormulationModal;
